import { useTheme } from '@mui/material';
import React, { createContext, useContext, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';

import { IMCContextType, DrawerType, DrawerTypes, PendingHelpAgentWidgetData } from './IMCContext.interface';

import useStatsigGate from 'modules/common/useStatsigGate';
import {
    DeepLinkMessageRejectReason,
    sanitizeDeepLinkMessage,
} from 'modules/IMC/HelpAgent/utils/sanitizeDeepLinkMessage';
import clearDeepLinkMessageParam from 'modules/IMC/HelpAgent/utils/clearDeepLinkMessageParam';
import { FeatureGates } from 'modules/common/constants/featuregates';
import {
    MessageCenterScreen,
    NavigationUpdateContextType,
    ParamsType,
    ScreenType,
} from 'modules/common/components/Navigator/context/NavigatorContext.interface';
import { collectiveTracking } from 'modules/common/components/AnalyticsTracking/utils';

const IMCContext = createContext<IMCContextType | undefined>(undefined);

/**
 * The help agent's analytics events, named in one place so the three stay together —
 * `Help Agent Engaged` is fired from useDecagonEmbed, the other two from this file.
 */
export const HELP_AGENT_ANALYTICS = {
    category: 'help_agent',
    OPENED: 'Help Agent Opened',
    CLOSED: 'Help Agent Closed',
    ENGAGED: 'Help Agent Engaged',
} as const;

const openEventByDrawerType = {
    messageCenter: {
        category: 'message_center',
        name: 'Message Center Opened',
    },
    helpAgent: {
        category: HELP_AGENT_ANALYTICS.category,
        name: HELP_AGENT_ANALYTICS.OPENED,
    },
    inbox: {
        category: 'inbox',
        name: 'Inbox Drawer Opened',
    },
};

const closeEventByDrawerType = {
    messageCenter: {
        category: 'message_center',
        name: 'Message Center Closed',
    },
    helpAgent: {
        category: HELP_AGENT_ANALYTICS.category,
        name: HELP_AGENT_ANALYTICS.CLOSED,
    },
    inbox: {
        category: 'inbox',
        name: 'Inbox Drawer Closed',
    },
};

const trackCloseDrawer = (drawer: DrawerType) => {
    if (!drawer) {
        return;
    }

    const { category, name } = closeEventByDrawerType[drawer];

    collectiveTracking.track({
        name,
        properties: {
            category,
        },
    });
};
const trackOpenDrawer = (
    drawer: DrawerType,
    props: {
        initialScreen?: string;
        source?: string;
        isHelpAgentWidgetOpen?: boolean;
    } = {}
) => {
    const { initialScreen, source, isHelpAgentWidgetOpen } = props;
    if (!drawer) {
        return;
    }

    const { category, name } = openEventByDrawerType[drawer];

    collectiveTracking.track({
        name,
        properties: {
            category,
            initial_screen: initialScreen,
            help_agent_experiment: 'widget',
            source,
            is_help_agent_open: isHelpAgentWidgetOpen ? 'true' : 'false',
        },
    });
};

interface UseIMCContextOptions {
    /**
     * Return `undefined` instead of throwing when there is no provider. For shared components that
     * read IMC state as a nicety and are also mounted outside the member app (e.g. the funnel).
     */
    optional?: boolean;
}

export function useIMCContext(): IMCContextType;
export function useIMCContext(options: { optional: true }): IMCContextType | undefined;
export function useIMCContext(options?: UseIMCContextOptions): IMCContextType | undefined {
    const context = useContext(IMCContext);
    if (!context && !options?.optional) {
        throw new Error('useIMCContext must be used within an IMCContextProvider');
    }
    return context;
}

export const parseTicketId = (value: string | null): number | null => {
    if (!value) {
        return null;
    }
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed <= 0 || String(parsed) !== value) {
        return null;
    }
    return parsed;
};

type PendingInitialScreenType = {
    drawer: DrawerType;
    screen: ScreenType;
    params?: ParamsType;
} | null;

const DEEP_LINK_MESSAGE_ACTION = 'help_agent_deeplink_message';

type DeepLinkMessageDetail = {
    outcome: 'accepted' | 'rejected' | 'paused';
    length: number;
    reason?: DeepLinkMessageRejectReason;
};

// Every outcome reports the same shape, so the fields that never vary (the raw length and the
// surface the link came from) are filled in here rather than repeated at each call site.
const reportDeepLinkMessage = (raw: string, detail: DeepLinkMessageDetail) =>
    datadogRum.addAction(DEEP_LINK_MESSAGE_ACTION, { ...detail, raw_length: raw.length, source: 'deep_link' });

/**
 * Decides what, if anything, the `?message=` param of a Help Agent deep link prefills.
 *
 * Every outcome is reported to RUM so a broken email template shows up as a spike in rejections
 * rather than silence. The payload carries the lengths and the reason only: the message is member
 * content and never leaves the page. `raw_length` is always the length of the param as it arrived,
 * so an outcome can be read against the input size without the raw string; `length` is what the
 * outcome measured (the cleaned length once the sanitizer ran).
 */
function resolveDeepLinkMessage(raw: string | null, isPaused: boolean): string | undefined {
    if (raw === null) {
        // A plain `?drawer=helpagent` link. There is nothing to report.
        return undefined;
    }

    if (isPaused) {
        reportDeepLinkMessage(raw, { outcome: 'paused', length: raw.length });
        return undefined;
    }

    const result = sanitizeDeepLinkMessage(raw);

    if (!result.ok) {
        reportDeepLinkMessage(raw, { outcome: 'rejected', reason: result.reason, length: result.length });
        return undefined;
    }

    reportDeepLinkMessage(raw, { outcome: 'accepted', length: result.message.length });

    return result.message;
}

export function IMCContextProvider({ children }: { children: React.ReactNode }) {
    // The Statsig client is Ready by the time the provider mounts, but the authenticated Statsig user
    // is applied later, in an AuthProvider effect, so the deep-link effect below evaluates this gate
    // against the anonymous user. It must therefore be used as a global on/off kill switch only:
    // user-level targeting on this gate would not have a user to target on the render that matters.
    const isDeepLinkMessagePaused = useStatsigGate(FeatureGates.HelpAgentDeeplinkMessagePaused);

    const urlParams = new URLSearchParams(window.location.search);
    const initialDrawer = urlParams.get('drawer') as DrawerType;
    // Only real drawers seed currOpenDrawer. helpAgent is opened as a floating widget (see the deep-link
    // effect below), not a drawer — seeding it would reserve an empty right-rail drawer region that lingers
    // after the widget closes. Whitelisting the real drawers (rather than just excluding helpAgent) also
    // keeps a malformed, user-controlled `?drawer=` value from creating an invalid drawer state.
    const [currOpenDrawer, setCurrOpenDrawer] = useState<DrawerType>(
        initialDrawer === DrawerTypes.MESSAGE_CENTER || initialDrawer === DrawerTypes.INBOX ? initialDrawer : null
    );
    const initialTicketId = initialDrawer && parseTicketId(urlParams.get('ticketId'));
    const [prevOpenDrawer, setPrevOpenDrawer] = useState<DrawerType>(null);
    const messageCenterNavigationRef = useRef<NavigationUpdateContextType | null>(null);
    const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
    const [isHelpAgentReady, setIsHelpAgentReady] = useState(false);
    const [pendingInitialScreen, setPendingInitialScreen] = useState<PendingInitialScreenType>(null);
    const [pendingHelpAgentMessage, setPendingHelpAgentMessage] = useState<string | null>(null);
    const [pendingHelpAgentWidgetData, setPendingHelpAgentWidgetData] = useState<PendingHelpAgentWidgetData | null>(
        null
    );
    const [activeRightSidebarId, setActiveRightSidebarId] = useState<string | null>(null);
    const [isHelpAgentWidgetOpen, setIsHelpAgentWidgetOpen] = useState(false);
    const [isHelpAgentFabHidden, setIsHelpAgentFabHidden] = useState(false);

    const theme = useTheme();

    const openDrawer = useCallback(
        (newDrawer: DrawerType, initialScreen?: ScreenType, params?: ParamsType) => {
            // Track closing of current drawer if new drawer is null
            if (newDrawer === null && currOpenDrawer !== null) {
                messageCenterNavigationRef.current?.clean();
                trackCloseDrawer(currOpenDrawer);
            }

            // Track opening of new drawer if it's different from current
            if (newDrawer !== null && newDrawer !== currOpenDrawer) {
                messageCenterNavigationRef.current?.clean();
                trackOpenDrawer(newDrawer, { initialScreen, isHelpAgentWidgetOpen });
            }

            // Update previous drawer state
            setPrevOpenDrawer(currOpenDrawer);

            // Update current drawer state
            setCurrOpenDrawer((prevDrawer) => {
                if (prevDrawer && newDrawer && newDrawer !== prevDrawer) {
                    // If switching between drawers, set to new drawer immediately
                    setCurrOpenDrawer(newDrawer);
                    return null;
                }
                // Toggle drawer state (open if different, close if same)
                return newDrawer === prevDrawer ? null : newDrawer;
            });

            // Navigate to initial screen if provided
            if (initialScreen) {
                setPendingInitialScreen({ drawer: newDrawer, screen: initialScreen, params });
            }
        },
        [messageCenterNavigationRef, currOpenDrawer, isHelpAgentWidgetOpen]
    );

    useEffect(() => {
        if (!pendingInitialScreen || currOpenDrawer !== pendingInitialScreen.drawer) {
            return () => {};
        }

        const timer = setTimeout(() => {
            // Only the message center navigates between screens; the others have nothing to drive.
            const navigatorMap: Record<DrawerTypes, NavigationUpdateContextType | null> = {
                [DrawerTypes.MESSAGE_CENTER]: messageCenterNavigationRef.current,
                [DrawerTypes.HELP_AGENT]: null,
                [DrawerTypes.INBOX]: null,
            };
            const navigator = navigatorMap[pendingInitialScreen.drawer as DrawerTypes];
            if (!navigator) {
                return;
            }

            const { screen, params } = pendingInitialScreen;
            navigator.clean();
            navigator.navigateToPage(MessageCenterScreen.HOME);
            navigator.navigateToPage(screen, params);
            setPendingInitialScreen(null);
        }, theme.transitions.duration.enteringScreen); // we gotta wait for the drawer to open if not it won't redirect properly to the screen

        return () => clearTimeout(timer);
    }, [pendingInitialScreen, currOpenDrawer, theme.transitions.duration.enteringScreen]);

    const registerRightSidebar = useCallback((id: string) => {
        setActiveRightSidebarId((currentId) => {
            // Dialog takes priority over global
            if (id === 'dialog') {
                return 'dialog';
            }
            // Only set to global if no active sidebar
            if (!currentId) {
                return 'global';
            }
            return currentId;
        });
    }, []);

    const unregisterRightSidebar = useCallback((id: string) => {
        setActiveRightSidebarId((currentId) => {
            // Only clear if this sidebar was active
            if (currentId === id) {
                // If dialog is closing, fall back to global
                // (assumes global sidebar is still mounted)
                if (id === 'dialog') {
                    return 'global';
                }
                return null;
            }
            return currentId;
        });
    }, []);

    const openHelpAgentWidget = useCallback((opts: { message?: string; source?: string } = {}) => {
        const { message, source } = opts;
        if (message) {
            setPendingHelpAgentWidgetData({ message, source });
        }
        setIsHelpAgentWidgetOpen(true);
        trackOpenDrawer(DrawerTypes.HELP_AGENT, { source });
    }, []);

    const closeHelpAgentWidget = useCallback(() => {
        setIsHelpAgentWidgetOpen(false);
        trackCloseDrawer(DrawerTypes.HELP_AGENT);
    }, []);

    const showHelpAgentFab = useCallback(() => {
        setIsHelpAgentFabHidden(false);
    }, []);

    const hideHelpAgentFab = useCallback(() => {
        setIsHelpAgentFabHidden(true);
    }, []);

    // Deep-link (e.g. from email): `/dashboard?drawer=helpAgent` opens the Help Agent chat on landing,
    // mirroring the `?drawer=messageCenter` behavior. Runs once on mount; the provider re-mounts on the
    // post-login full reload, so the param still takes effect after the auth round-trip. This is the single
    // surface-agnostic seam — repoint it here when the chat interface that replaces IMC lands.
    useEffect(() => {
        // Match case-insensitively: production's server lowercases this deep-link to `helpagent`, so a
        // strict `=== 'helpAgent'` comparison would miss it and the widget would never open in prod (it
        // works in staging, which preserves the casing).
        // The optional `?message=` param is sanitized before it prefills the chat, and dropped
        // entirely while the HelpAgentDeeplinkMessagePaused kill switch is on. Either way the widget
        // still opens, so a bad message costs the member a prefill, never the deep link itself.
        if (initialDrawer?.toLowerCase() === DrawerTypes.HELP_AGENT.toLowerCase()) {
            const message = resolveDeepLinkMessage(urlParams.get('message'), isDeepLinkMessagePaused);
            if (message === undefined) {
                // Rejected or paused: the message will never be delivered, on this visit or after a
                // login round-trip, so there is nothing left to preserve and the param goes now. An
                // accepted message is stripped later instead, once Decagon has actually received it
                // (see HelpAgentWidget). No `message` param at all also lands here, where the helper
                // is a no-op.
                clearDeepLinkMessageParam();
            }
            openHelpAgentWidget({ message, source: 'deep_link' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo<IMCContextType>(
        () => ({
            isHelpAgentReady,
            setIsHelpAgentReady,
            currOpenDrawer,
            prevOpenDrawer,
            openDrawer,
            messageCenterNavigationRef,
            hasUnreadMessages,
            setHasUnreadMessages,
            initialTicketId,
            pendingHelpAgentMessage,
            setPendingHelpAgentMessage,
            pendingHelpAgentWidgetData,
            setPendingHelpAgentWidgetData,
            activeRightSidebarId,
            registerRightSidebar,
            unregisterRightSidebar,
            isHelpAgentWidgetOpen,
            openHelpAgentWidget,
            closeHelpAgentWidget,
            isHelpAgentFabHidden,
            showHelpAgentFab,
            hideHelpAgentFab,
        }),
        [
            isHelpAgentReady,
            setIsHelpAgentReady,
            currOpenDrawer,
            prevOpenDrawer,
            openDrawer,
            hasUnreadMessages,
            initialTicketId,
            pendingHelpAgentMessage,
            pendingHelpAgentWidgetData,
            activeRightSidebarId,
            registerRightSidebar,
            unregisterRightSidebar,
            isHelpAgentWidgetOpen,
            openHelpAgentWidget,
            closeHelpAgentWidget,
            isHelpAgentFabHidden,
            showHelpAgentFab,
            hideHelpAgentFab,
        ]
    );

    return <IMCContext.Provider value={value}>{children}</IMCContext.Provider>;
}
